import type { ReactNode } from 'react'
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import type { AuthoringBlockInstance } from './types'

export const authoringPointerSensorOptions = {
  activationConstraint: { distance: 6 },
} as const

export type AuthoringDocumentProps = {
  blocks: AuthoringBlockInstance[]
  onReorder: (next: AuthoringBlockInstance[]) => void
  /** Render one block; receive drag props for sortable chrome. */
  renderBlock: (
    block: AuthoringBlockInstance,
    drag: {
      setNodeRef: (node: HTMLElement | null) => void
      style: React.CSSProperties
      isDragging: boolean
      dragHandleProps: React.HTMLAttributes<HTMLButtonElement>
    },
  ) => ReactNode
  /** Ids that participate in sorting (defaults to non-pinned blocks). */
  sortableIds?: string[]
  className?: string
}

function SortableBlockRow({
  block,
  renderBlock,
}: {
  block: AuthoringBlockInstance
  renderBlock: AuthoringDocumentProps['renderBlock']
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    disabled: Boolean(block.pinned),
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  const dragHandleProps = block.pinned
    ? undefined
    : ({ ...attributes, ...listeners } as React.HTMLAttributes<HTMLButtonElement>)

  return (
    <>
      {renderBlock(block, {
        setNodeRef,
        style,
        isDragging,
        dragHandleProps: dragHandleProps ?? ({} as React.HTMLAttributes<HTMLButtonElement>),
      })}
    </>
  )
}

/**
 * Vertical sortable document of authoring blocks (dnd-kit).
 * Pinned blocks stay in place and are excluded from reorder by default.
 */
export function AuthoringDocument({
  blocks,
  onReorder,
  renderBlock,
  sortableIds,
  className,
}: AuthoringDocumentProps) {
  const sensors = useSensors(useSensor(PointerSensor, authoringPointerSensorOptions))
  const ids = sortableIds ?? blocks.filter((b) => !b.pinned).map((b) => b.id)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = blocks.findIndex((b) => b.id === active.id)
    const newIndex = blocks.findIndex((b) => b.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    if (blocks[oldIndex]?.pinned || blocks[newIndex]?.pinned) return
    onReorder(arrayMove(blocks, oldIndex, newIndex))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={className ?? 'space-y-3'}>
          {blocks.map((block) =>
            block.pinned ? (
              <div key={block.id}>
                {renderBlock(block, {
                  setNodeRef: () => {},
                  style: {},
                  isDragging: false,
                  dragHandleProps: {} as React.HTMLAttributes<HTMLButtonElement>,
                })}
              </div>
            ) : (
              <SortableBlockRow key={block.id} block={block} renderBlock={renderBlock} />
            ),
          )}
        </div>
      </SortableContext>
    </DndContext>
  )
}
